// Add Item — TRIMIE Hi-Fi v1 (per docs/design-spec.md §4.9)
//
// Landing: two-up grid of source cards
//   - Upload  → camera/library picker (+ CameraCapture modal on desktop)
//   - From URL → paste product link, server fetches the garment image
//
// Tapping a card opens the corresponding flow. Back arrow returns to the chooser.

import { useState, useRef } from 'react';
import api from './api';
import CameraCapture from './CameraCapture';
import Header from './components/Header';

// Fixed taxonomy — must match UserRowClothImage.CATEGORY_CHOICES on the backend.
export const CATEGORIES = [
  { value: 'tops',      label: 'Tops' },
  { value: 'bottoms',   label: 'Bottoms' },
  { value: 'dresses',   label: 'Dresses' },
  { value: 'outerwear', label: 'Outerwear' },
  { value: 'shoes',     label: 'Shoes' },
  { value: 'bags',      label: 'Bags' },
  { value: 'other',     label: 'Other' },
];

export default function AddClothingPage({ onNav, onAdded }) {
  // mode: null (chooser) | 'upload' | 'url'
  const [mode, setMode] = useState(null);

  // Upload flow state
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [label, setLabel] = useState('');
  const [category, setCategory] = useState('other');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const inputRef = useRef(null);

  // URL import flow state
  const [importUrl, setImportUrl] = useState('');
  const [importCategory, setImportCategory] = useState('other');
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState(null);
  const [importSuccess, setImportSuccess] = useState(null);

  const applyFile = (f) => {
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) { setError('Please select an image.'); return; }
    setLoading(true);
    setError(null);
    const form = new FormData();
    form.append('image', file);
    form.append('label', label || 'Untitled');
    form.append('category', category);
    try {
      await api.post('/tryon/upload-row-clothes/', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setFile(null); setPreview(null); setLabel(''); setCategory('other');
      if (onAdded) onAdded();
      onNav('closet');
    } catch (err) {
      setError(err.response?.data?.message || 'Upload failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async (e) => {
    e.preventDefault();
    const url = importUrl.trim();
    if (!url) return;
    setImporting(true);
    setImportError(null);
    setImportSuccess(null);
    try {
      const res = await api.post('/tryon/import-garment/', { url, category: importCategory });
      setImportSuccess(res.data?.message || 'Garment imported!');
      setImportUrl(''); setImportCategory('other');
      if (onAdded) onAdded();
    } catch (err) {
      setImportError(err.response?.data?.message || 'Import failed. Try pasting a direct image URL.');
    } finally {
      setImporting(false);
    }
  };

  const backToChooser = () => {
    setMode(null);
    setError(null);
    setImportError(null);
    setImportSuccess(null);
  };

  return (
    <div className="screen">
      {showCamera && (
        <CameraCapture
          label="Photograph Garment"
          onCapture={(f) => { applyFile(f); setShowCamera(false); }}
          onClose={() => setShowCamera(false)}
        />
      )}

      <Header
        title="Add Item"
        onBack={mode ? backToChooser : () => onNav('closet')}
      />

      {/* ── Source chooser ─────────────────────────────────────────────── */}
      {!mode && (
        <div className="add-source-grid">
          <button className="add-source-card" onClick={() => setMode('upload')} type="button">
            <div className="add-source-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" />
                <circle cx="12" cy="13" r="4" />
              </svg>
            </div>
            <div className="add-source-title">Upload</div>
            <div className="add-source-sub">From camera or library</div>
          </button>

          <button className="add-source-card" onClick={() => setMode('url')} type="button">
            <div className="add-source-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 14l-3 3a2 2 0 0 1-3-3l5-5a2 2 0 0 1 3 0" />
                <path d="M14 10l3-3a2 2 0 0 1 3 3l-5 5a2 2 0 0 1-3 0" />
              </svg>
            </div>
            <div className="add-source-title">From URL</div>
            <div className="add-source-sub">Paste a product link</div>
          </button>
        </div>
      )}

      {/* ── Upload flow ────────────────────────────────────────────────── */}
      {mode === 'upload' && (
        <div className="add-screen">
          <div className="upload-drop-zone" onClick={() => inputRef.current?.click()}>
            {preview ? (
              <img src={preview} alt="preview" />
            ) : (
              <>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4">
                  <rect x="3" y="3" width="18" height="18" rx="3" />
                  <path d="M3 15l5-5 4 4 3-3 5 4" strokeLinejoin="round" />
                  <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor" />
                </svg>
                <span>Tap to upload garment image</span>
              </>
            )}
            {preview && (
              <button
                className="upload-icon-btn"
                onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
                type="button"
                aria-label="Change photo"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" />
                  <circle cx="12" cy="13" r="4" />
                </svg>
              </button>
            )}
          </div>

          <div style={{ display: 'flex', gap: 10, marginBottom: 4 }}>
            <button type="button" className="btn-outline" style={{ flex: 1 }} onClick={() => inputRef.current?.click()}>
              Library
            </button>
            <button type="button" className="btn-outline" style={{ flex: 1 }} onClick={() => setShowCamera(true)}>
              Camera
            </button>
          </div>
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            hidden
            onChange={(e) => { const f = e.target.files[0]; if (f) applyFile(f); }}
          />

          <form className="add-form" onSubmit={handleSubmit}>
            <div>
              <label className="form-label" htmlFor="garment-label">Label</label>
              <input
                id="garment-label"
                type="text"
                placeholder="e.g. Black blazer, summer dress…"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
              />
            </div>

            <div>
              <span className="form-label">Category</span>
              <div className="cat-chip-row">
                {CATEGORIES.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    className={`cat-chip${category === c.value ? ' active' : ''}`}
                    onClick={() => setCategory(c.value)}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>

            {error && <div className="alert error">{error}</div>}
            <button className="btn-purple btn-lg" type="submit" disabled={loading || !file}>
              {loading
                ? <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}><span className="spinner" />Saving…</span>
                : 'Save to closet'}
            </button>
          </form>
        </div>
      )}

      {/* ── URL import flow ────────────────────────────────────────────── */}
      {mode === 'url' && (
        <div className="add-screen">
          <p className="add-url-helper">
            Paste a product page URL or a direct image link. We'll pull the garment photo for you.
          </p>

          <form onSubmit={handleImport} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <label className="form-label">Product URL</label>
              <input
                type="url"
                placeholder="https://www.zara.com/…"
                value={importUrl}
                onChange={(e) => { setImportUrl(e.target.value); setImportError(null); setImportSuccess(null); }}
                style={{ width: '100%', boxSizing: 'border-box' }}
                autoComplete="off"
                spellCheck={false}
              />
            </div>

            <div>
              <span className="form-label">Category</span>
              <div className="cat-chip-row">
                {CATEGORIES.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    className={`cat-chip${importCategory === c.value ? ' active' : ''}`}
                    onClick={() => setImportCategory(c.value)}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>

            {importError && <div className="alert error">{importError}</div>}
            {importSuccess && (
              <div className="alert success">
                ✓ {importSuccess}
                <button
                  type="button"
                  className="btn-ghost"
                  style={{ marginTop: 8, width: '100%' }}
                  onClick={() => onNav('closet')}
                >
                  View in closet →
                </button>
              </div>
            )}

            <button className="btn-purple btn-lg" type="submit" disabled={importing || !importUrl.trim()}>
              {importing
                ? <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}><span className="spinner" />Importing…</span>
                : 'Import garment'}
            </button>
          </form>

          <p className="add-url-tip">
            If import fails, right-click the garment image on the retailer site → "Copy image address" → paste that URL.
          </p>
        </div>
      )}
    </div>
  );
}
